import { TrackingContext } from '@ps/web-core-analytics';
import classNames from 'clsx';
import React, { useContext } from 'react';

import { Race } from '@swf/casino';
import Counter from '@swf/components/Counter/Counter';
import { gameEntity, raceEntity } from '@swf/core/analytics';
import type { CasinoRace as CasinoRaceType } from '@swf/core/types/CasinoRaces';
import { isFinished, isRunning } from '@swf/core/utils/casino';

import Table from '../Table/Table';

import styles from './RacesGrid.scss';

interface Props {
  leaderboardVisible?: boolean;
  races: CasinoRaceType[];
  onLeaderboard: (racePath: string) => () => void;
  widgetId?: string;
  path: string;
}

const RACES_COLS = 3;

const RacesGrid: React.FC<Props> = ({
  races = [],
  onLeaderboard,
  leaderboardVisible,
  widgetId,
  path
}) => {
  const trackingContext = useContext(TrackingContext);

  return (
    <div className={styles.container}>
      <Counter>
        {(count) => (
          <div className={styles.grid}>
            {races.map((race, index) => {
              const {
                startTime,
                endTime,
                status,
                prizeDetails,
                leaderboard,
                userRaceStanding,
                multiGameList
              } = race;
              const showRace =
                (isRunning(status) ? endTime : startTime) > count &&
                !isFinished(status, !userRaceStanding);

              const standings = new Array(RACES_COLS).fill({});
              if (leaderboard) {
                leaderboard
                  .slice(0, RACES_COLS)
                  .forEach((standing, i) => (standings[i] = standing));
              }
              if (standings && userRaceStanding && userRaceStanding.rank > RACES_COLS) {
                standings[RACES_COLS - 1] = userRaceStanding;
              }

              return showRace ? (
                <TrackingContext.Provider
                  key={index}
                  value={{
                    ...trackingContext,
                    entities:
                      multiGameList?.length === 1
                        ? [raceEntity(race), gameEntity(race.gameInfo)]
                        : [raceEntity(race)]
                  }}
                >
                  <div
                    key={index}
                    className={classNames(
                      styles.race,
                      isRunning(race.status) && leaderboardVisible
                        ? styles.leaderboardRace
                        : styles.nextRace,
                      { [styles.myRace]: isRunning(status) }
                    )}
                  >
                    <Race
                      count={count}
                      race={race}
                      onLeaderboard={onLeaderboard(`${path}[${index}]`)}
                      widgetId={widgetId}
                      path={`${path}[${index}]`}
                    />
                    {leaderboardVisible && isRunning(status) && standings && (
                      <Table
                        medal={false}
                        userStanding={userRaceStanding}
                        standings={standings}
                        prizeDetails={prizeDetails}
                      />
                    )}
                  </div>
                </TrackingContext.Provider>
              ) : null;
            })}
          </div>
        )}
      </Counter>
    </div>
  );
};

export default RacesGrid;
